import { useMemo } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, Check } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import {
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { TierChip } from "@/components/cinch/TierChip";
import { cn } from "@/lib/utils";

import type { ExpansionSearch } from "..";
import { useExpansion, useExpansionCsmOptions, type ExpansionAccount } from "../api";
import {
  filterAccounts,
  fmtContacts,
  needsAttention,
  sortAccounts,
  type ExpansionFilters,
  type SortKey,
} from "../lib";
import { ExpansionAccountDrawer } from "../components/ExpansionAccountDrawer";
import { fmtCur, fmtCur2, monthLabel } from "../../renewals/format";

const SORT_LABELS: { key: SortKey; label: string }[] = [
  { key: "bill", label: "Largest gap" },
  { key: "over", label: "Most contacts over" },
  { key: "name", label: "A–Z" },
  { key: "tier", label: "Tier" },
  { key: "usage", label: "Usage" },
];

export function ExpansionPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/expansion" }) as ExpansionSearch;

  const setSearch = (next: Partial<ExpansionSearch>) =>
    navigate({
      to: "/expansion",
      search: (prev: ExpansionSearch) => ({ ...prev, ...next }),
    });

  const data = useExpansion();
  const csmOptions = useExpansionCsmOptions();

  const filters: ExpansionFilters = useMemo(
    () => ({
      q: search.q ?? "",
      tiers: (search.tiers ?? "")
        .split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 5),
      csm: search.csm ?? null,
      overOnly: !!search.over,
      attention: !!search.attention,
    }),
    [search],
  );
  const sort: SortKey = search.sort ?? "bill";

  const accounts = useMemo(() => data.data?.accounts ?? [], [data.data]);

  // KPI cards reflect the working cohort (tier/CSM filters) but ignore the
  // search box and the cards' own toggles, so clicking a card never changes
  // the cards themselves (same convention as Upsell Ops).
  const cardPool = useMemo(
    () => filterAccounts(accounts, { ...filters, q: "", overOnly: false, attention: false }),
    [accounts, filters],
  );
  const rows = useMemo(
    () => sortAccounts(filterAccounts(accounts, filters), sort),
    [accounts, filters, sort],
  );

  const stats = useMemo(() => {
    const over = cardPool.filter((a) => (a.overage_contacts ?? 0) > 0);
    return {
      gap: cardPool.reduce(
        (s, a) => s + (a.amount_to_bill != null && a.amount_to_bill > 0 ? a.amount_to_bill : 0),
        0,
      ),
      overCount: over.length,
      overContacts: over.reduce((s, a) => s + (a.overage_contacts ?? 0), 0),
      attention: cardPool.filter(needsAttention).length,
    };
  }, [cardPool]);

  const csmComboOptions = useMemo<ComboOption[]>(
    () =>
      (csmOptions.data?.options ?? []).map((o) => ({
        id: o.value,
        label: o.label,
        group: o.kind === "partner" ? "Managing partners" : "CSMs",
        badge: String(o.account_count),
      })),
    [csmOptions.data],
  );
  const selectedCsmLabel = csmComboOptions.find((o) => o.id === filters.csm)?.label;

  const columns: DataTableColumn<ExpansionAccount>[] = [
    {
      key: "name",
      header: "Account",
      className: "max-w-[320px]",
      render: (a) => (
        <div>
          <button
            type="button"
            className="text-left text-sm font-semibold hover:text-brand hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setSearch({ account: a.qb_id ?? undefined, expanded: true });
            }}
            title="Open the full account view"
          >
            {a.name ?? a.account_id}
          </button>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {a.flags.no_contract
              ? "No New/Replacement contract"
              : `${a.contract_type} · ${a.contract_signed_at ?? "—"}`}
            {needsAttention(a) && (
              <span
                className="inline-flex items-center gap-0.5 rounded border border-warning/40 bg-warning/10 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-warning"
                title={
                  a.flags.no_contract
                    ? "No governing contract — nothing to bill against"
                    : a.flags.no_allotment
                      ? "Contract has no contact allotment — overage can't be computed"
                      : "Contract has no $/10k price — overage can't be priced"
                }
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                fix
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "tier",
      header: "Tier",
      align: "center",
      render: (a) => <TierChip tier={a.tier} />,
    },
    {
      key: "csm",
      header: "CSM",
      render: (a) => (
        <span className={cn("text-sm", !a.csm_label && "text-muted-foreground")}>
          {a.csm_label ?? "—"}
        </span>
      ),
    },
    {
      key: "contracted",
      header: "Contracted",
      align: "right",
      render: (a) => (
        <span className="text-sm tabular-nums">
          {fmtContacts(a.contracted_contacts)}
          {a.upsell_contacts > 0 && (
            <span
              className="ml-1 text-xs text-brand"
              title={`incl. ${fmtContacts(a.upsell_contacts)} contacts added by upsell`}
            >
              +up
            </span>
          )}
        </span>
      ),
    },
    {
      key: "usage",
      header: "Usage",
      align: "right",
      render: (a) => (
        <div className="text-sm tabular-nums">
          {fmtContacts(a.usage)}
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {a.uses_email_count ? "emails" : "contacts"}
          </div>
        </div>
      ),
    },
    {
      key: "over",
      header: "Over",
      align: "right",
      render: (a) =>
        a.overage_contacts == null ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <div className="text-sm tabular-nums">
            <span className={cn(a.overage_contacts > 0 ? "font-semibold text-orange" : "text-muted-foreground")}>
              {fmtContacts(a.overage_contacts)}
            </span>
            {a.overage_contacts > 0 && (
              <div className="text-[10px] text-muted-foreground">
                {a.overage_blocks} × {a.ten_k_more != null ? fmtCur2(a.ten_k_more) : "$?"} /10k
              </div>
            )}
          </div>
        ),
    },
    {
      key: "billed",
      header: "Billed now",
      align: "right",
      render: (a) => <span className="text-sm tabular-nums">{fmtCur2(a.currently_billed)}</span>,
    },
    {
      key: "should",
      header: "Per contract",
      align: "right",
      render: (a) => (
        <span className="text-sm tabular-nums">
          {a.contracted_total != null ? fmtCur2(a.contracted_total) : "—"}
        </span>
      ),
    },
    {
      key: "to_bill",
      header: "To bill",
      align: "right",
      render: (a) =>
        a.amount_to_bill == null ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <span
            className={cn(
              "text-sm font-bold tabular-nums",
              a.amount_to_bill > 0.005
                ? "text-success"
                : a.amount_to_bill < -0.005
                  ? "text-destructive"
                  : "text-muted-foreground",
            )}
          >
            {fmtCur2(a.amount_to_bill)}
          </span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expansion</h1>
          <p className="text-sm text-muted-foreground">
            Contracted-contacts overage across actively billing accounts — what
            each contract says we should bill vs what we're billing today.
          </p>
        </div>
        {data.data && (
          <div className="text-right text-xs text-muted-foreground">
            Billing month: <b className="text-foreground">{monthLabel(data.data.month)}</b>
            {" · "}Source: BI.REVENUE_WATERFALL + BILLING.BILLING_CONTRACTS
          </div>
        )}
      </div>

      {data.data && !data.data.usage_data_available && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Contact usage is unavailable:</b> the warehouse role can't read
            the customers view (row access policy), so Usage and Over show 0
            for every account and the gap below only reflects base-fee
            true-ups. Contracts and billing amounts are still accurate.
          </span>
        </div>
      )}

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label="Monthly expansion gap"
          value={fmtCur(stats.gap)}
          sub="Sum of positive to-bill amounts"
        />
        <KpiTile
          label="Over allotment"
          value={stats.overCount}
          sub={`of ${cardPool.length} billing accounts`}
          onClick={() => setSearch({ over: filters.overOnly ? undefined : true })}
          selected={filters.overOnly}
        />
        <KpiTile
          label="Contacts over contract"
          value={fmtContacts(stats.overContacts)}
          sub="Across all over-allotment accounts"
        />
        <KpiTile
          label="Needs attention"
          value={stats.attention}
          sub="Missing contract, allotment, or $/10k"
          onClick={() => setSearch({ attention: filters.attention ? undefined : true })}
          selected={filters.attention}
        />
      </div>

      <FilterBar>
        <FilterSearch
          placeholder="Search account name…"
          value={filters.q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Tier" count={filters.tiers.length}>
          <CheckboxList
            options={[1, 2, 3, 4, 5].map((t) => ({ value: String(t), label: `T${t}` }))}
            selected={filters.tiers.map(String)}
            onChange={(v) => setSearch({ tiers: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        <FilterChip label="CSM" valueLabel={selectedCsmLabel}>
          <Combobox
            options={csmComboOptions}
            value={filters.csm}
            onChange={(v) => setSearch({ csm: v ?? undefined })}
            placeholder="All CSMs"
            searchPlaceholder="Search CSMs…"
            emptyText={csmOptions.isLoading ? "Loading…" : "No CSMs found."}
          />
        </FilterChip>
        <FilterChip
          label="More"
          count={(filters.overOnly ? 1 : 0) + (filters.attention ? 1 : 0)}
        >
          <CheckboxList
            options={[
              { value: "over", label: "Over allotment only" },
              { value: "attention", label: "Needs attention only" },
            ]}
            selected={[
              ...(filters.overOnly ? ["over"] : []),
              ...(filters.attention ? ["attention"] : []),
            ]}
            onChange={(v) =>
              setSearch({
                over: v.includes("over") || undefined,
                attention: v.includes("attention") || undefined,
              })
            }
          />
        </FilterChip>
        <div className="flex-1" />
        <FilterChip
          label="Sort"
          valueLabel={
            sort !== "bill" ? SORT_LABELS.find((s) => s.key === sort)?.label : undefined
          }
        >
          <div className="flex flex-col gap-0.5">
            {SORT_LABELS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSearch({ sort: s.key === "bill" ? undefined : s.key })}
                className={cn(
                  "flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm",
                  sort === s.key ? "bg-brand-light font-medium text-brand" : "hover:bg-muted",
                )}
              >
                {s.label}
                {sort === s.key && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        </FilterChip>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(a) => a.account_id}
        loading={data.isLoading}
        error={data.error ? String(data.error) : undefined}
        emptyMessage="No accounts match."
        onRowClick={(a) => a.qb_id != null && setSearch({ account: a.qb_id })}
        selectedKey={
          search.account != null
            ? rows.find((a) => a.qb_id === search.account)?.account_id
            : undefined
        }
      />

      <p className="text-xs text-muted-foreground">
        {rows.length} account{rows.length === 1 ? "" : "s"} shown ·{" "}
        {fmtCur2(
          rows.reduce(
            (s, a) => s + (a.amount_to_bill != null && a.amount_to_bill > 0 ? a.amount_to_bill : 0),
            0,
          ),
        )}{" "}
        combined monthly to-bill · a positive amount means the contract supports
        billing more than the current invoice; negative means we bill above the
        contracted total.
      </p>

      <ExpansionAccountDrawer accounts={accounts} />
    </div>
  );
}
